import { IconButton, Tooltip } from '@mui/material';
import { Icon } from '@shared/ui';
import clsx from 'clsx';
import type { FC } from 'react';
import { currentSearchShortcutLabel } from './adminSearchShortcut';

type AdminSearchButtonProps = {
  onClick: () => void;
  /** Matches the sidebar's own two states: 226px labelled rows, or the 72px icon rail. */
  isExpanded: boolean;
};

export const ADMIN_SEARCH_BUTTON_LABEL = 'Search admin';

/**
 * The palette's click affordance, living in the nav rather than on the pages.
 *
 * `NavLink` and `SideBarButton` both wrap a router `Link` and require a route, but
 * search is an action, not a destination — hence this sibling. Styling deliberately
 * mirrors those two so the row does not read as a foreign object in the nav.
 */
const AdminSearchButton: FC<AdminSearchButtonProps> = ({ onClick, isExpanded }) => {
  const shortcut = currentSearchShortcutLabel();
  const title = `${ADMIN_SEARCH_BUTTON_LABEL} (${shortcut})`;

  if (!isExpanded) {
    return (
      <Tooltip placement="right" title={title}>
        <IconButton
          aria-keyshortcuts="Meta+K Control+K"
          aria-label={title}
          className="size-12 rounded-2xl text-sage-100 transition-colors hover:bg-white/10"
          onClick={onClick}
        >
          <Icon>search</Icon>
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <button
      aria-keyshortcuts="Meta+K Control+K"
      aria-label={title}
      // This app does not run Tailwind preflight, so the UA button styles are reset here.
      className={clsx(
        'flex w-full cursor-pointer appearance-none items-center gap-2.5 rounded-[11px]',
        'border-0 bg-transparent p-1 text-left font-sans transition-colors hover:bg-white/10'
      )}
      onClick={onClick}
      type="button"
    >
      <span className="flex size-8 items-center justify-center rounded-[11px] text-sage-200">
        <Icon className="text-[20px] text-sage-200">search</Icon>
      </span>
      <span className="flex-1 text-[13px] font-medium text-white">Search</span>
      <kbd className="mr-1 rounded border border-white/20 bg-black/20 px-1 py-px font-mono text-[10px] text-white/70">
        {shortcut}
      </kbd>
    </button>
  );
};

export default AdminSearchButton;
